import { inject, Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import sortBy from 'lodash-es/sortBy';

export namespace NConsultants {
    export const ENDPOINTS = {
        getList: 'consultants/getList'
    };

    export interface IConsultantData {
        id: number;
        firstName: string;
        lastName: string;
    }

    export interface IConsultant extends IConsultantData {
        label: string;
    }

    export const someoneLastname = 'Someone';
    export const anyoneLastname = 'Anyone';
}

@Injectable({
    providedIn: 'root'
})
export class ConsultantsService {
    private consultants?: NConsultants.IConsultant[] = undefined;

    private requestService = inject(RequestService);

    async loadConsultants(): Promise<NConsultants.IConsultantData[]> {
        if (this.consultants) {
            return this.consultants;
        }

        try {
            let response = await this.requestService.send(undefined, { path: NConsultants.ENDPOINTS.getList });
            if (Array.isArray(response) && response.length > 0) {
                response = response.filter(i => !!i && typeof i === 'object' && i.lastName);
                const consultants: NConsultants.IConsultant[] = [];

                const anyone = response.find(i => this.isAnyone(i));
                if (anyone) {
                    consultants.push({ ...anyone, label: anyone.firstName.trim() + ' ' + anyone.lastName.trim() });
                }

                const someone = response.find(i => this.isSomeone(i));
                if (someone) {
                    consultants.push({ ...someone, label: someone.firstName.trim() + ' ' + someone.lastName.trim() });
                }

                const others = sortBy(
                    response.filter(i => !this.isSomeone(i) && !this.isAnyone(i)),
                    'lastName'
                );

                consultants.push(...others.map(i => ({ ...i, label: [i.firstName, i.lastName].join(' ') })));

                this.consultants = consultants;
            }
            return this.getConsultants();
        } catch (e) {
            return [];
        }
    }

    getConsultants(): NConsultants.IConsultant[] {
        return this.consultants || [];
    }

    private isSomeone(consultant: NConsultants.IConsultantData): boolean {
        const fullName = `${consultant.firstName} ${consultant.lastName}`.toLowerCase();
        return fullName.includes(NConsultants.someoneLastname.toLowerCase());
    }

    private isAnyone(consultant: NConsultants.IConsultantData): boolean {
        const fullName = `${consultant.firstName} ${consultant.lastName}`.toLowerCase();
        return fullName.includes(NConsultants.anyoneLastname.toLowerCase());
    }
}
